import { type KeyboardEvent, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import ChevronLine from '@/assets/icons/chevron_line.svg?component';
import CloseLine24 from '@/assets/icons/close_line_24.svg?component';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import { Menu, MenuContent, MenuTrigger } from '@/components/molecules/Menu';
import {
    AUDIT_SCOPE_KINDS,
    type AuditLogFilters,
    type AuditScopeKind,
    type AuditScopeOption,
    addAuditFilters,
    applyAuditFilterChange,
    joinAuditTexts,
    scopeFilter,
    scopeFilterLabel,
    useAuditText,
} from '@/domain/auditLog';

import CellIcon from '../cells/CellIcon';

import type { AuditFilterBindings } from './filterBindings';
import ScopeSearch from './ScopeSearch';

import styles from './ScopePicker.module.scss';

const SCOPE_KEY = {
    client: 'clientId',
    location: 'locationId',
    cage: 'cageId',
} as const satisfies Record<AuditScopeKind, keyof AuditLogFilters>;

interface ScopePickerProps {
    bindings: AuditFilterBindings;
    label: string;
    /**
     * The id of a visible label beside the picker, as a form row has. The toggle then
     * shows only the place, takes its name from that label and the place, and fills its
     * slot like an input.
     */
    labelledBy?: string;
}

/**
 * "Where": one search over clients, sites and pens that resolves to exactly one id, so two
 * pens both called "Pen 3" are never mixed up. The write path fills in the parents, so a
 * site also finds the entries of its pens.
 */
export default function ScopePicker({ bindings, label, labelledBy }: ScopePickerProps) {
    const { filters, labelSources, onChange, rememberScope } = bindings;
    const { t } = useTranslation();
    const text = useAuditText();
    // React's ids hold colons, which jsdom's selector engine cannot match in tests.
    const valueId = useId().replaceAll(':', '');
    const [isOpen, setIsOpen] = useState(false);
    const toggleRef = useRef<HTMLElement | null>(null);

    const chosen = AUDIT_SCOPE_KINDS.flatMap((kind) => {
        const id = filters[SCOPE_KEY[kind]];
        return id === undefined ? [] : [scopeFilterLabel(kind, id, labelSources)];
    });
    const value = chosen.length > 0 ? text(joinAuditTexts(chosen)) : null;

    function closeAndRefocus() {
        setIsOpen(false);
        toggleRef.current?.focus();
    }

    function handlePick(option: AuditScopeOption) {
        rememberScope(option);
        // A pick replaces whichever level is set, so there is only ever one.
        const change = addAuditFilters(scopeFilter(option.kind, option.id));
        onChange(applyAuditFilterChange(filters, change));
        closeAndRefocus();
    }

    // The button goes once nothing is set, which would drop focus to the page itself.
    function handleClear() {
        const next: AuditLogFilters = { ...filters };
        for (const kind of AUDIT_SCOPE_KINDS) delete next[SCOPE_KEY[kind]];
        toggleRef.current?.focus();
        onChange(next);
    }

    // The arrows open the picker, as they open a select.
    function handleToggleKeyDown(event: KeyboardEvent<HTMLElement>) {
        if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && !isOpen) {
            event.preventDefault();
            setIsOpen(true);
        }
    }

    // The clear button cannot sit inside the toggle, so a gap in the toggle holds its place
    // before the chevron, and the button is laid over that gap.
    return (
        <div className={classNames(styles.picker, { [styles.field]: labelledBy !== undefined })}>
            <Menu kind="panel" isOpen={isOpen} onOpenChange={setIsOpen} className={styles.menu}>
                <MenuTrigger
                    variant="unstyled"
                    triggerRef={toggleRef}
                    className={styles.toggle}
                    aria-haspopup={false}
                    aria-labelledby={
                        labelledBy === undefined ? undefined : `${labelledBy} ${valueId}`
                    }
                    onKeyDown={handleToggleKeyDown}>
                    {labelledBy === undefined && (
                        <>
                            <span className={styles.toggleLabel}>{label}</span>{' '}
                        </>
                    )}
                    <span
                        id={valueId}
                        className={value === null ? styles.placeholder : styles.value}>
                        {value ?? t('auditLog.filters.wherePlaceholder')}
                    </span>
                    {value !== null && <span className={styles.clearSlot} />}
                    <SvgIcon icon={ChevronLine} size="small" className={styles.chevron} />
                </MenuTrigger>
                {/* A group, not a menu: a menu may own only menu items. */}
                <MenuContent
                    tag="fieldset"
                    role="group"
                    aria-label={label}
                    className={styles.popup}
                    offset={4}>
                    {isOpen && (
                        <ScopeSearch label={label} onPick={handlePick} onClose={closeAndRefocus} />
                    )}
                </MenuContent>
            </Menu>
            {value !== null && (
                <button
                    type="button"
                    className={styles.clear}
                    aria-label={t('auditLog.chips.remove', { label, value })}
                    onClick={handleClear}>
                    <CellIcon icon={CloseLine24} />
                </button>
            )}
        </div>
    );
}
